import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import useIsVisible from '@aytm/lib/hooks/useIsVisible.js'

import { useComponentLayoutState, LayoutContext } from '../states/useComponentLayout'
import { useSelectedItemsState, SelectedItemsContext } from '../states/useSelectedItems'
import { useImagesState, ImagesContext } from '../states/useImages'
import { useSigtestState, SigtestContext } from '../states/useSigtest'
import { useModesState, ModesContext } from '../states/useModes'
import { useTableState, TableContext } from '../states/useTable'

import CompareItemsHeader from './CompareItemsHeader'
import CompareItemsModesBar from './CompareItemsModesBar'
import CompareItemsTable from './CompareItemsTable'
import CompareItemsFullscreenFrame from './CompareItemsFullscreenFrame'

import { CompareItemsProps } from '../types'

import style from './CompareItems.module.scss'

export default function CompareItems(props: CompareItemsProps) {
  const layout = useComponentLayoutState(props)
  const modes = useModesState(props)
  const selectedItems = useSelectedItemsState(props)
  const images = useImagesState(props, { fullScreen: modes.fullScreen, selectedItems: selectedItems.items })
  const sigtest = useSigtestState(props, { selectedItems: selectedItems.items, analysisMode: modes.analysisMode })
  const table = useTableState(props, {
    layout,
    modes,
    selectedItems,
    images,
    sigtest
  })

  const contentHolderForFullscreenRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (
      modes.fullScreen &&
      contentHolderForFullscreenRef.current &&
      !contentHolderForFullscreenRef.current.style.height
    ) {
      contentHolderForFullscreenRef.current.style.height = `${layout.measures?.visualContentHeight || 0}px`
    }
  }, [modes.fullScreen, layout.measures])

  if ('development' === process.env.NODE_ENV) {
    // eslint-disable-next-line no-console
    console.debug('CompareItems states:', {
      layout,
      modes,
      selectedItems,
      images,
      sigtest,
      table
    })
  }

  if (layout.showComponent) {
    const component = (
      <LayoutContext.Provider value={layout}>
        <ModesContext.Provider value={modes}>
          <SelectedItemsContext.Provider value={selectedItems}>
            <ImagesContext.Provider value={images}>
              <SigtestContext.Provider value={sigtest}>
                <TableContext.Provider value={table}>
                  <div
                    className={style.CompareItems}
                    id={props.id ? `CompareItems-${props.id}` : null}
                    data-compare-items="root"
                  >
                    <CompareItemsHeader
                      title={props.title}
                      showExportButton={props.showExportButton}
                      onExportData={props.onExportData}
                    />
                    <CompareItemsModesBar
                      enableSigtestAnalysis={props.enableSigtestAnalysis}
                      mainAnalysisName={props.mainAnalysisName}
                      sigtestAnalysisName={props.sigtestAnalysisName}
                      presets={props.presets}
                      attributes={props.attributes}
                      canUseImages={false !== props.canUseImages}
                    />
                    <CompareItemsTable />
                  </div>
                </TableContext.Provider>
              </SigtestContext.Provider>
            </ImagesContext.Provider>
          </SelectedItemsContext.Provider>
        </ModesContext.Provider>
      </LayoutContext.Provider>
    )

    return modes.fullScreen ? (
      <>
        <div ref={contentHolderForFullscreenRef} />
        {createPortal(
          <CompareItemsFullscreenFrame title={props.fullScreenTitle} onClose={() => void modes.setFullScreen(false)}>
            {component}
          </CompareItemsFullscreenFrame>,
          document.body
        )}
      </>
    ) : (
      component
    )
  } else if (null == layout.showComponent) {
    return <CompareItemsPlaceholder onVisible={() => void layout.setShowComponent(true)} />
  } else {
    return null
  }
}

const CompareItemsPlaceholderStyle = { display: 'block', width: '100%', height: '1px', background: 'transparent' }
function CompareItemsPlaceholder({ onVisible }: { onVisible?: () => void }) {
  const elementRef = useRef<HTMLDivElement>(null)
  const isVisible = useIsVisible(elementRef)
  const onVisibleRef = useRef<() => void>(onVisible)
  onVisibleRef.current = onVisible

  useEffect(() => {
    if (isVisible) {
      onVisibleRef.current?.()
    }
  }, [isVisible])

  return (
    <div style={CompareItemsPlaceholderStyle} ref={elementRef}>
      &nbsp;
    </div>
  )
}
